/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: telephonyServersApi.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    API calls for Telephony Servers module.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  TelephonyServer,
  ListTelephonyServersResponse,
  TelephonyServerFormData,
  FetchTelephonyServersParams,
} from '../types';

export async function fetchTelephonyServers(
  params: FetchTelephonyServersParams,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<ListTelephonyServersResponse> {
  const searchParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy || 'name',
    sortOrder: params.sortOrder || 'asc',
  });

  if (params.search) {
    searchParams.set('search', params.search);
  }

  const response = await fetch(`${buildApiUrl('/api/telephony-servers')}?${searchParams.toString()}`, {
    method: 'GET',
    headers: getApiHeaders(tenantSlug, userId),
  });

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || response.statusText);
  }

  return response.json();
}

export async function fetchTelephonyServerById(
  id: number | string,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<TelephonyServer> {
  const response = await fetch(buildApiUrl(`/api/telephony-servers/${id}`), {
    method: 'GET',
    headers: getApiHeaders(tenantSlug, userId),
  });

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || response.statusText);
  }

  const result = await response.json();
  return result.data;
}

export async function createTelephonyServer(
  data: TelephonyServerFormData,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<TelephonyServer> {
  const response = await fetch(buildApiUrl('/api/telephony-servers'), {
    method: 'POST',
    headers: getApiHeaders(tenantSlug, userId),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to create telephony server');
  }

  const result = await response.json();
  return result.data;
}

export async function updateTelephonyServer(
  id: number | string,
  data: Partial<TelephonyServerFormData>,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<TelephonyServer> {
  const response = await fetch(buildApiUrl(`/api/telephony-servers/${id}`), {
    method: 'PUT',
    headers: getApiHeaders(tenantSlug, userId),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to update telephony server');
  }

  const result = await response.json();
  return result.data;
}

export async function deleteTelephonyServer(
  id: number | string,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<void> {
  const response = await fetch(buildApiUrl(`/api/telephony-servers/${id}`), {
    method: 'DELETE',
    headers: getApiHeaders(tenantSlug, userId),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to delete telephony server');
  }
}

export async function verifyTelephonyServer(
  id: number | string,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<any> {
  const response = await fetch(buildApiUrl(`/api/telephony-servers/${id}/verify`), {
    method: 'POST',
    headers: getApiHeaders(tenantSlug, userId),
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || response.statusText);
  }

  return response.json();
}

export async function fetchTelephonyServersQuickList(
  search: string,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<TelephonyServer[]> {
  const queryParams = new URLSearchParams({
    ...(search && { search }),
  });

  const response = await fetch(
    `${buildApiUrl('/api/telephony-servers/quicklist')}?${queryParams}`,
    {
      method: 'GET',
      headers: getApiHeaders(tenantSlug, userId),
    },
  );

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(message || response.statusText);
  }

  const result = await response.json();
  return result.data;
}

